import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useLanguageContext } from './LanguageContext';

// Import the built-in manifest for offline-first experience
import { translationsManifest } from '../data/translations-manifest';

const CACHE_KEY = 'lilhero_translations_v2';
const VERSION_KEY = 'lilhero_translations_version_v2';

// Translations index: key → { fr, en, es, de, it, pt, nl }
interface TranslationsIndex {
  [key: string]: {
    [lang: string]: string;
  };
}

interface TranslationContextType {
  /**
   * Translate a key to the current language
   * @param key - The translation key (e.g., "home.books")
   * @param fallback - English fallback text shown if translation not found
   * @param replacements - Optional object for variable replacements
   * @returns The translated string
   *
   * Usage: t("home.books", "Books")
   * Usage: t("profile.hello_name", "Hello {name}", { name: "Alice" })
   */
  t: (key: string, fallback?: string, replacements?: Record<string, string | number>) => string;
  loading: boolean;
  ready: boolean;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

export const TranslationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { language } = useLanguageContext();
  const [index, setIndex] = useState<TranslationsIndex>(() => {
    // Initialize with built-in manifest for instant availability
    return translationsManifest as TranslationsIndex;
  });
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(true); // Start ready with built-in manifest

  // Load translations on startup
  useEffect(() => {
    const loadTranslations = async () => {
      try {
        // 1. Load from local cache (instant)
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          // Merge cached with built-in (cached takes priority)
          setIndex(prev => ({ ...prev, ...parsed }));
          console.log('📦 Translations loaded from cache');
        }

        // 2. Check remote version
        const { data: metadata } = await supabase
          .from('ui_translations_metadata')
          .select('version')
          .single();

        const localVersion = localStorage.getItem(VERSION_KEY);
        const remoteVersion = metadata?.version;

        // 3. If new version → fetch complete
        if (!localVersion || !remoteVersion || Number(localVersion) < remoteVersion) {
          console.log('🔄 Downloading translations...');

          const { data, error } = await supabase
            .from('ui_translations')
            .select('key, translations');

          if (error) {
            console.warn('⚠️ Error fetching translations:', error);
          } else if (data && data.length > 0) {
            // Build the index (key → translations)
            const newIndex: TranslationsIndex = {};
            data.forEach(({ key, translations }) => {
              if (key && translations) {
                newIndex[key] = translations;
              }
            });

            // Save to cache
            localStorage.setItem(CACHE_KEY, JSON.stringify(newIndex));
            localStorage.setItem(VERSION_KEY, String(remoteVersion || 1));

            // Merge with existing (remote takes priority)
            setIndex(prev => ({ ...prev, ...newIndex }));
            console.log(`✅ ${Object.keys(newIndex).length} translations indexed`);
          }
        }

        setReady(true);
      } catch (err) {
        console.warn('⚠️ Error loading translations:', err);
        setReady(true); // Continue with built-in/cached translations
      } finally {
        setLoading(false);
      }
    };

    loadTranslations();
  }, []);

  /**
   * Translates a key to the current language
   *
   * New format: t("common.english_fallback", "English fallback")
   * Old format (deprecated): t("common.french_text", "French text") - still works for backwards compat
   */
  const t = useCallback((
    key: string,
    fallback?: string,
    replacements?: Record<string, string | number>
  ): string => {
    // Look up by key
    const translations = index[key];

    let result: string;

    if (translations) {
      // Found in index - use translation for current language
      result = translations[language] || translations.en || translations.fr || fallback || key;
    } else {
      // Not found - use fallback or key
      result = fallback || key;
    }

    // Apply replacements
    if (replacements) {
      for (const [placeholder, value] of Object.entries(replacements)) {
        result = result.replace(new RegExp(`\\{${placeholder}\\}`, 'g'), String(value));
      }
    }

    return result;
  }, [language, index]);

  return (
    <TranslationContext.Provider value={{ t, loading, ready }}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within TranslationProvider');
  }
  return context;
};
